import type {
  DataResponse,
  PaginatedResponse,
  TrackerActivityQuery,
  TrackerDay,
  TrackerHealth,
  TrackerImage,
  TrackerImagesQuery,
  TrackerPruneInput,
  TrackerPruneResult,
  TrackerStats,
  TrackerUser,
  TrackerUserPatchInput,
} from '@pms/shared';
import { api } from '@/lib/api';

// PMS backend proxy for the external desktop tracker; the admin secret stays on the server.
const BASE = '/tracking';

function userPath(userId: string): string {
  return `${BASE}/users/${encodeURIComponent(userId)}`;
}

export const trackingService = {
  async health(): Promise<TrackerHealth> {
    const res = await api.get<DataResponse<TrackerHealth>>(`${BASE}/health`);
    return res.data.data;
  },

  async stats(): Promise<TrackerStats> {
    const res = await api.get<DataResponse<TrackerStats>>(`${BASE}/stats`);
    return res.data.data;
  },

  async activity(query: TrackerActivityQuery): Promise<TrackerDay[]> {
    const res = await api.get<DataResponse<TrackerDay[]>>(`${BASE}/activity`, { params: query });
    return res.data.data;
  },

  async users(): Promise<TrackerUser[]> {
    const res = await api.get<DataResponse<TrackerUser[]>>(`${BASE}/users`);
    return res.data.data;
  },

  async updateUser(userId: string, patch: TrackerUserPatchInput): Promise<TrackerUser> {
    const res = await api.patch<DataResponse<TrackerUser>>(userPath(userId), patch);
    return res.data.data;
  },

  async removeUser(userId: string): Promise<void> {
    await api.delete(userPath(userId));
  },

  async images(query: Partial<TrackerImagesQuery>): Promise<PaginatedResponse<TrackerImage>> {
    const res = await api.get<PaginatedResponse<TrackerImage>>(`${BASE}/images`, { params: query });
    return res.data;
  },

  /** Relative to the axios base — for `AuthImage`, which sends the Bearer token. */
  imageFilePath(imageId: string): string {
    return `${BASE}/images/${encodeURIComponent(imageId)}/file`;
  },

  async prune(input: TrackerPruneInput): Promise<TrackerPruneResult> {
    const res = await api.post<DataResponse<TrackerPruneResult>>(`${BASE}/prune`, input);
    return res.data.data;
  },
};
